import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Building2, Mail } from "lucide-react";

import Header from "../components/layout/Header";
import SiteFooter from "../components/layout/SiteFooter";
import { useAuth } from "../hooks/useAuth";

// Company Information
const COMPANY_INFO = {
  name: "CLOUD COMPUTING TECHNOLOGY SP Z O O",
  address: "Ul. Al. Aleje Jerozolimskie 109/70, 02-011 Warsaw, Poland",
  email: "support@music-producer.ai",
};

function PolicyLayout({ title, eyebrow, children }) {
  const { isAuthed, user, clearAuth } = useAuth();
  return (
    <div className="app-shell v2-page">
      <div className="v2-page-bg" aria-hidden="true">
        <div className="v2-hero-glow v2-glow-1" />
        <div className="v2-hero-glow v2-glow-2" />
      </div>
      <Header isAuthed={isAuthed} user={user} onLogout={clearAuth} onOpenAccount={() => {}} />

      <main className="v2-main">
        <section className="v2-page-hero">
          <div className="v2-container">
            <div style={{ textAlign: "left", maxWidth: 780, margin: "0 auto" }}>
              <Link to="/" className="v2-back-link">
                <ArrowLeft size={14} /> Back to Home
              </Link>
            </div>
            {eyebrow && <span className="v2-pill">{eyebrow}</span>}
            <h1 className="v2-page-title">{title}</h1>
          </div>
        </section>

        <section className="v2-page-body">
          <div className="v2-container">
            <div className="v2-policy-doc">
              <span className="v2-policy-updated">
                <Calendar size={12} /> Last updated: {new Date().toISOString().slice(0, 10)}
              </span>
              {children}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function ContactCard({ intro }) {
  return (
    <div className="v2-contact-section">
      {intro && <p>{intro}</p>}
      <div className="v2-contact-card">
        <h4><Building2 size={12} /> Company Information</h4>
        <p className="v2-company-name">{COMPANY_INFO.name}</p>
        <p className="v2-company-address">{COMPANY_INFO.address}</p>
        <p><Mail size={13} style={{ verticalAlign: "-2px", marginRight: 6, color: "#6ee7b7" }} />
          <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
        </p>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <PolicyLayout title="Privacy Policy" eyebrow="Legal">
      <p>AI Music Maker ("we", "us", "our") respects your privacy. This Policy explains how we collect, use, and protect your information when you use our website and services.</p>

      <h3>1. Information We Collect</h3>
      <ul>
        <li><strong>Account data:</strong> name, email address (collected via Google Sign-In)</li>
        <li><strong>Usage data:</strong> actions within the app, generated content metadata, timestamps</li>
        <li><strong>Payment data:</strong> handled securely by our payment processor Paddle.com; we do not store or have access to your full card numbers or payment details</li>
      </ul>

      <h3>2. How We Use Information</h3>
      <ul>
        <li>To provide, maintain, and improve our AI music generation services</li>
        <li>To process payments and manage subscriptions through Paddle.com</li>
        <li>To communicate important updates, respond to support requests, and send service-related notifications</li>
        <li>To analyze usage patterns and improve user experience</li>
      </ul>

      <h3>3. Data Retention</h3>
      <p>We retain personal data for as long as your account is active or as needed to provide our services and comply with legal obligations. You may request deletion of your data at any time.</p>

      <h3>4. Third-Party Services</h3>
      <p>We use the following trusted third-party providers:</p>
      <ul>
        <li><strong>Paddle.com:</strong> Payment processing and subscription management. Paddle is the Merchant of Record for all transactions. View their privacy policy at <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">paddle.com/legal/privacy</a></li>
        <li><strong>Google:</strong> Authentication services</li>
        <li><strong>AI Music API providers:</strong> Song, lyrics, and video generation</li>
      </ul>

      <h3>5. Your Rights (GDPR)</h3>
      <p>If you are in the EU/EEA, you have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data ("right to be forgotten")</li>
        <li>Object to or restrict processing of your data</li>
        <li>Data portability</li>
      </ul>
      <p>To exercise these rights, contact us using the details below.</p>

      <h3>6. Security</h3>
      <p>We implement industry-standard administrative, technical, and physical safeguards to protect your data, including SSL/TLS encryption. However, no method of transmission over the Internet is 100% secure.</p>

      <h3>7. Cookies</h3>
      <p>We use essential cookies to maintain your session and preferences. We do not use tracking or advertising cookies.</p>

      <h3>8. Changes to This Policy</h3>
      <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page with an updated revision date.</p>

      <h3>9. Contact Us</h3>
      <ContactCard intro="For privacy-related questions or requests:" />
    </PolicyLayout>
  );
}

export function TermsPage() {
  return (
    <PolicyLayout title="Terms of Service" eyebrow="Legal">
      <h3>1. Agreement</h3>
      <p>By accessing or using AI Music Maker (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>

      <h3>2. Service Description</h3>
      <p>AI Music Maker is a web-based software service that uses artificial intelligence to generate songs, lyrics, and music videos based on user prompts. The Service enables users to:</p>
      <ul>
        <li>Generate AI-composed songs from text descriptions or custom lyrics</li>
        <li>Generate AI-written lyrics from descriptions</li>
        <li>Generate AI music videos and clips</li>
        <li>Create AI voice covers</li>
        <li>Download and use generated content</li>
      </ul>
      <p><strong>Limitations:</strong> Generated content quality may vary. The AI may not always produce desired results. Generation times typically range from 30 seconds to several minutes depending on content type.</p>

      <h3>3. Payment Processing — Paddle.com</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>
      <p>All payments, billing, and subscription management are handled by Paddle. By making a purchase, you also agree to Paddle's <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">Buyer Terms</a>.</p>

      <h3>4. Accounts</h3>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate information and promptly update it if it changes.</p>

      <h3>5. Subscriptions & Billing</h3>
      <ul>
        <li>Subscriptions renew automatically at the end of each billing period unless canceled</li>
        <li>You may cancel your subscription at any time; access continues until the end of the current billing period</li>
        <li>Applicable taxes will be added based on your location</li>
        <li>Price changes will be communicated in advance</li>
      </ul>

      <h3>6. Refunds</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> for subscription purchases. See our <Link to="/refund">Refund Policy</Link> for details.</p>

      <h3>7. Acceptable Use</h3>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service to generate content that is illegal, harmful, threatening, abusive, defamatory, or infringes on intellectual property rights</li>
        <li>Attempt to interfere with the normal operation of the Service</li>
        <li>Access the Service using automated methods other than our provided interface</li>
        <li>Resell or redistribute the Service without authorization</li>
        <li>Use the Service to create content that impersonates real individuals without consent</li>
      </ul>

      <h3>8. Intellectual Property</h3>
      <p>Subject to your compliance with these Terms:</p>
      <ul>
        <li>You retain rights to your original input (prompts, custom lyrics)</li>
        <li>You receive a license to use AI-generated content for personal or commercial purposes</li>
        <li>We retain rights to the underlying AI models and Service technology</li>
      </ul>
      <p>Generated content may not be unique; similar outputs may be generated for other users.</p>

      <h3>9. Disclaimer of Warranties</h3>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE UNINTERRUPTED, ERROR-FREE, OR SECURE OPERATION.</p>

      <h3>10. Limitation of Liability</h3>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.</p>

      <h3>11. Changes to Terms</h3>
      <p>We may update these Terms from time to time. We will notify you of material changes. Continued use after changes constitutes acceptance.</p>

      <h3>12. Governing Law</h3>
      <p>These Terms are governed by the laws of Poland. Any disputes shall be resolved through appropriate legal channels.</p>

      <h3>13. Contact Us</h3>
      <ContactCard intro="For questions about these Terms:" />
    </PolicyLayout>
  );
}

export function RefundPage() {
  return (
    <PolicyLayout title="Refund Policy" eyebrow="Legal">
      <h3>1. Payment Processing</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>

      <h3>2. 30-Day Money-Back Guarantee</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> on all subscription purchases. If you are not satisfied with our Service within the first 30 days of your subscription, you may request a full refund.</p>
      <p>To request a refund within the 30-day period:</p>
      <ul>
        <li>Contact us at <strong>{COMPANY_INFO.email}</strong> with your order details</li>
        <li>Or contact Paddle support directly at <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">paddle.com/support</a></li>
      </ul>

      <h3>3. Subscription Cancellations</h3>
      <p>You may cancel your subscription at any time:</p>
      <ul>
        <li>Access to the Service continues until the end of your current billing period</li>
        <li>No partial refunds are provided for unused time after the 30-day guarantee period</li>
        <li>To cancel, use the "Manage subscription" button in your account or contact support</li>
      </ul>

      <h3>4. One-Time Credit Purchases</h3>
      <ul>
        <li>Credit purchases are final once credits have been used</li>
        <li>If purchased in error and credits are unused, contact support within 14 days for a refund</li>
      </ul>

      <h3>5. Chargebacks</h3>
      <p>Please contact {COMPANY_INFO.email} before initiating a chargeback. We're happy to help resolve billing issues directly.</p>

      <h3>6. How to Request a Refund</h3>
      <p>To request a refund, please provide:</p>
      <ul>
        <li>Your order ID or transaction reference</li>
        <li>The email address used for purchase</li>
        <li>Reason for the refund request (optional but helpful)</li>
      </ul>

      <h3>7. Refund Processing Time</h3>
      <p>Refunds are typically processed within 5-10 business days. The time it takes for the refund to appear in your account depends on your payment method and financial institution.</p>

      <h3>8. Contact Us</h3>
      <ContactCard intro="For refund requests or billing questions:" />
      <p className="v2-policy-note">Please include your order ID or the email address used for purchase when contacting support.</p>
    </PolicyLayout>
  );
}
